import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { t } from '../i18n';
import { deadLetters, onQueueChange, pending } from '../offline';

/**
 * "N updates waiting to send" — the one thing offline.ts was built to make
 * visible and nothing ever rendered. Without this, a queued write is
 * invisible: the tap looked like it worked, and there was no way to see
 * that anything was still outstanding until it silently landed later.
 *
 * Dead-lettered writes (rejected, identity mismatch, retries exhausted) get
 * their own count: "queued" and "gave up" are opposite messages, and folding
 * the second into the first is how a lost write stays lost quietly.
 */
export function OutboxBanner() {
  const [count, setCount] = useState(0);
  const [dead, setDead] = useState(0);

  useEffect(() => {
    let live = true;
    const check = () => {
      void pending().then((writes) => {
        if (live) setCount(writes.length);
      });
      void deadLetters().then((writes) => {
        if (live) setDead(writes.length);
      });
    };
    check();
    const unsubscribe = onQueueChange(check);
    return () => {
      live = false;
      unsubscribe();
    };
  }, []);

  if (count === 0 && dead === 0) return null;

  return (
    // biome-ignore lint/a11y/useSemanticElements: <output> is tied to a form calculation; this banner announces queue-count changes, which is exactly what role="status" (implicit aria-live="polite") is for.
    <div
      role="status"
      className="mb-4 flex items-center gap-2 rounded-2xl bg-secondary px-4 py-2.5 text-xs font-semibold text-muted-foreground"
    >
      <Icon
        icon={count > 0 ? 'ph:cloud-arrow-up-bold' : 'ph:cloud-warning-bold'}
        className="shrink-0 text-base"
      />
      <span>
        {count > 0 && t('{{n}} waiting to send').replace('{{n}}', String(count))}
        {count > 0 && dead > 0 && ' · '}
        {dead > 0 && t('{{n}} could not send').replace('{{n}}', String(dead))}
      </span>
    </div>
  );
}
